/**
 * The workout's sounds: chimes through the Web Audio API. No speech (owner: no voice; an optional Smash-style
 * announcer is todo 10). In the Android app they play over YouTube Music without pausing or ducking it: the WebView
 * plays Web Audio as a media player without asking for audio focus (checked in todo 4, see docs/HANDOVER.md).
 */
import type { Cue } from "../core/workout";

let ctx: AudioContext | undefined;

/** Browsers start audio only after a tap: call this from tap handlers (it is cheap after the first time). */
export function unlockAudio() {
  try {
    if (!ctx) {
      ctx = new AudioContext();
      // older iOS only unlocks after something was played inside the tap: one silent sample
      const src = ctx.createBufferSource();
      src.buffer = ctx.createBuffer(1, 1, 22050);
      src.connect(ctx.destination);
      src.start();
    }
    // "suspended" before the first tap, "interrupted" on iOS after a call or after the app was in the background
    if (ctx.state !== "running") void ctx.resume();
  } catch {
    // no audio: the workout works without sound
  }
}

/**
 * Unlocks audio on every tap until it runs (iOS: audio may only start inside a tap, and it is interrupted again when
 * the app was in the background), so the first chime of a rest started by a timer is not lost.
 */
export function unlockAudioOnTap() {
  for (const type of ["touchend", "click", "keydown"]) {
    document.addEventListener(type, () => { if (ctx?.state !== "running") unlockAudio(); }, { capture: true, passive: true });
  }
}

/**
 * Soft chimes instead of plain beeps (owner: the first beeps were shrill, above all in headphones): low notes, a gentle
 * attack, a bell-like fade and a low-pass filter. Each entry is a list of [note in Hz, start offset in s]; the count is
 * one short C5, "go" rises C5–G5, the end is a falling G4–C4.
 */
const CHIMES = {
  count: { notes: [[523, 0]], len: 0.35 },
  go: { notes: [[523, 0], [784, 0.12]], len: 0.6 },
  end: { notes: [[392, 0], [262, 0.18]], len: 0.9 },
} as const;
const VOLUME = 0.25;

function beep(kind: keyof typeof CHIMES) {
  unlockAudio();
  if (!ctx) return;
  const { notes, len } = CHIMES[kind];
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 2000;
  filter.connect(ctx.destination);
  for (const [freq, offset] of notes) {
    const t0 = ctx.currentTime + offset;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(VOLUME, t0 + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + len);
    gain.connect(filter);
    // the note plus a quiet octave above gives a bell-like, rounder sound than a bare sine
    for (const [mult, level] of [[1, 1], [2, 0.15]] as const) {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq * mult;
      g.gain.value = level;
      osc.connect(g).connect(gain);
      osc.start(t0);
      osc.stop(t0 + len + 0.05);
    }
  }
}

/** Plays the beeps; call-outs (`c.call`) are left for the optional announcer (todo 10). */
export function playCues(cues: Cue[]) {
  for (const c of cues) if (c.beep) beep(c.beep);
}
